import React from 'react';
import { Link } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import './SecurityPage.css';

const SecurityPage: React.FC = () => {
  usePageTitle('Security Policy');
  const lastUpdated = "December 29, 2024";

  return (
    <div className="security-page">
      <section className="security-hero">
        <div className="container">
          <span className="section-label">Legal</span>
          <h1>Security Policy</h1>
          <p className="security-subtitle">
            Our commitment to protecting your data with enterprise-grade security
          </p>
          <p className="last-updated">Last Updated: {lastUpdated}</p>
        </div>
      </section>

      <section className="security-content">
        <div className="container">
          <div className="policy-wrapper">
            <nav className="policy-nav">
              <h4>Contents</h4>
              <ul>
                <li><a href="#overview">Security Overview</a></li>
                <li><a href="#infrastructure">Infrastructure Security</a></li>
                <li><a href="#data-protection">Data Protection</a></li>
                <li><a href="#access-control">Access Control</a></li>
                <li><a href="#encryption">Encryption Standards</a></li>
                <li><a href="#compliance">Compliance & Certifications</a></li>
                <li><a href="#monitoring">Monitoring & Detection</a></li>
                <li><a href="#incident-response">Incident Response</a></li>
                <li><a href="#vendor-security">Vendor Security</a></li>
                <li><a href="#physical-security">Physical Security</a></li>
                <li><a href="#business-continuity">Business Continuity</a></li>
                <li><a href="#responsible-disclosure">Responsible Disclosure</a></li>
              </ul>
            </nav>

            <div className="policy-body">
              <section id="overview" className="policy-section">
                <h2>1. Security Overview</h2>
                <p>
                  At JuryView, security is foundational to everything we build. We understand that our
                  clients entrust us with sensitive legal case data, and we take this responsibility
                  seriously. Our security program is designed to protect the confidentiality, integrity,
                  and availability of all data processed through our platform.
                </p>
                <p>
                  We employ a defense-in-depth approach, implementing multiple layers of security controls
                  across our infrastructure, applications, and operations. Our security practices are
                  regularly audited and continuously improved to address evolving threats and maintain
                  compliance with industry standards.
                </p>
                <div className="security-highlights">
                  <div className="highlight-item">
                    <span className="highlight-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                      </svg>
                    </span>
                    <span className="highlight-text">SOC 2 Type II Compliant</span>
                  </div>
                  <div className="highlight-item">
                    <span className="highlight-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                    </span>
                    <span className="highlight-text">AES-256 Encryption</span>
                  </div>
                  <div className="highlight-item">
                    <span className="highlight-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                        <polyline points="22 4 12 14.01 9 11.01"/>
                      </svg>
                    </span>
                    <span className="highlight-text">99.9% Uptime SLA</span>
                  </div>
                </div>
              </section>

              <section id="infrastructure" className="policy-section">
                <h2>2. Infrastructure Security</h2>

                <h3>2.1 Cloud Infrastructure</h3>
                <p>
                  JuryView is hosted on enterprise-grade cloud infrastructure provided by industry-leading
                  providers (AWS and Google Cloud Platform), both of which maintain comprehensive security
                  certifications including SOC 2, ISO 27001, and FedRAMP.
                </p>
                <ul>
                  <li>Geographically distributed data centers with redundancy</li>
                  <li>Virtual private cloud (VPC) isolation for all customer environments</li>
                  <li>Network segmentation and micro-segmentation</li>
                  <li>DDoS protection and Web Application Firewall (WAF)</li>
                  <li>Automated security patching and updates</li>
                </ul>

                <h3>2.2 Network Security</h3>
                <ul>
                  <li>All traffic encrypted using TLS 1.3</li>
                  <li>Strict firewall rules with default-deny policies</li>
                  <li>Intrusion detection and prevention systems (IDS/IPS)</li>
                  <li>Network traffic monitoring and anomaly detection</li>
                  <li>Regular vulnerability scanning and penetration testing</li>
                </ul>
              </section>

              <section id="data-protection" className="policy-section">
                <h2>3. Data Protection</h2>

                <h3>3.1 Data Classification</h3>
                <p>
                  All data is classified according to sensitivity level, with appropriate security
                  controls applied based on classification:
                </p>
                <ul>
                  <li><strong>Confidential:</strong> Case data, client information, analysis results</li>
                  <li><strong>Internal:</strong> Operational data, system configurations</li>
                  <li><strong>Public:</strong> Marketing materials, published research</li>
                </ul>

                <h3>3.2 Data Isolation</h3>
                <p>
                  Client data is logically isolated using separate database schemas and encryption keys.
                  No client can access another client's data, and our internal teams access client data
                  only when necessary for support purposes and with explicit authorization.
                </p>

                <h3>3.3 Data Minimization</h3>
                <p>
                  We collect and retain only the data necessary to provide our services. Case data can
                  be deleted upon client request, and we provide tools for clients to manage their own
                  data retention policies.
                </p>
              </section>

              <section id="access-control" className="policy-section">
                <h2>4. Access Control</h2>

                <h3>4.1 Authentication</h3>
                <ul>
                  <li>Multi-factor authentication (MFA) required for all accounts</li>
                  <li>Strong password policies with complexity requirements</li>
                  <li>Support for enterprise SSO (SAML 2.0, OAuth 2.0, OpenID Connect)</li>
                  <li>Session management with automatic timeout</li>
                  <li>Account lockout after failed authentication attempts</li>
                </ul>

                <h3>4.2 Authorization</h3>
                <ul>
                  <li>Role-based access control (RBAC) with granular permissions</li>
                  <li>Principle of least privilege enforced across all systems</li>
                  <li>Regular access reviews and privilege audits</li>
                  <li>Separation of duties for sensitive operations</li>
                </ul>

                <h3>4.3 Employee Access</h3>
                <p>
                  JuryView employees undergo background checks and security training before being granted
                  system access. Access is limited to what is necessary for job functions and is promptly
                  revoked upon role change or departure.
                </p>
              </section>

              <section id="encryption" className="policy-section">
                <h2>5. Encryption Standards</h2>

                <h3>5.1 Encryption at Rest</h3>
                <ul>
                  <li>AES-256 encryption for all stored data</li>
                  <li>Customer-managed encryption keys available for enterprise clients</li>
                  <li>Encrypted database backups with separate key management</li>
                  <li>Hardware Security Modules (HSMs) for key storage</li>
                </ul>

                <h3>5.2 Encryption in Transit</h3>
                <ul>
                  <li>TLS 1.3 for all API and web traffic</li>
                  <li>Certificate pinning for mobile applications</li>
                  <li>Perfect forward secrecy (PFS) enabled</li>
                  <li>HSTS headers enforced on all endpoints</li>
                </ul>

                <h3>5.3 Key Management</h3>
                <p>
                  Encryption keys are managed through a dedicated key management service with automatic
                  rotation, audit logging, and strict access controls. Keys are never stored alongside
                  encrypted data.
                </p>
              </section>

              <section id="compliance" className="policy-section">
                <h2>6. Compliance & Certifications</h2>
                <p>
                  JuryView maintains compliance with industry standards and undergoes regular third-party
                  audits to verify our security controls.
                </p>

                <h3>Current Certifications & Compliance</h3>
                <ul>
                  <li><strong>SOC 2 Type II:</strong> Annual audit covering security, availability, and confidentiality</li>
                  <li><strong>GDPR:</strong> Compliant with European data protection requirements</li>
                  <li><strong>CCPA:</strong> Compliant with California Consumer Privacy Act</li>
                  <li><strong>HIPAA:</strong> Business Associate Agreement available for healthcare-related matters</li>
                </ul>

                <h3>Audit Reports</h3>
                <p>
                  SOC 2 Type II audit reports are available to clients and prospects under NDA.
                  Contact <a href="mailto:security@juryview.com">security@juryview.com</a> to request
                  a copy.
                </p>
              </section>

              <section id="monitoring" className="policy-section">
                <h2>7. Monitoring & Detection</h2>

                <h3>7.1 Security Monitoring</h3>
                <ul>
                  <li>24/7 security operations center (SOC) monitoring</li>
                  <li>Security Information and Event Management (SIEM) system</li>
                  <li>Automated alerting for suspicious activities</li>
                  <li>Real-time threat intelligence integration</li>
                </ul>

                <h3>7.2 Logging & Audit Trails</h3>
                <ul>
                  <li>Comprehensive audit logging for all system activities</li>
                  <li>Immutable log storage with integrity verification</li>
                  <li>Log retention for compliance and forensic purposes</li>
                  <li>Client-accessible audit logs for their own activities</li>
                </ul>

                <h3>7.3 Vulnerability Management</h3>
                <ul>
                  <li>Continuous vulnerability scanning</li>
                  <li>Regular penetration testing by third-party firms</li>
                  <li>Automated dependency scanning for known vulnerabilities</li>
                  <li>Defined SLAs for vulnerability remediation based on severity</li>
                </ul>
              </section>

              <section id="incident-response" className="policy-section">
                <h2>8. Incident Response</h2>
                <p>
                  JuryView maintains a comprehensive incident response plan that is regularly tested
                  and updated. Our incident response process includes:
                </p>

                <h3>8.1 Response Process</h3>
                <ul>
                  <li><strong>Detection:</strong> Automated monitoring and alerting systems</li>
                  <li><strong>Triage:</strong> Immediate assessment and severity classification</li>
                  <li><strong>Containment:</strong> Rapid isolation of affected systems</li>
                  <li><strong>Eradication:</strong> Removal of threat and vulnerability remediation</li>
                  <li><strong>Recovery:</strong> Restoration of services with validation</li>
                  <li><strong>Post-Incident:</strong> Root cause analysis and process improvement</li>
                </ul>

                <h3>8.2 Notification</h3>
                <p>
                  In the event of a security incident affecting client data, we will notify affected
                  clients within 72 hours of confirmed impact, in compliance with applicable regulations.
                  Notifications will include the nature of the incident, affected data, and remediation
                  steps taken.
                </p>
              </section>

              <section id="vendor-security" className="policy-section">
                <h2>9. Vendor Security</h2>
                <p>
                  Third-party vendors with access to JuryView systems or data undergo rigorous security
                  assessments before engagement and are subject to ongoing monitoring.
                </p>
                <ul>
                  <li>Security questionnaire and documentation review</li>
                  <li>SOC 2 or equivalent certification verification</li>
                  <li>Contractual security and privacy requirements</li>
                  <li>Regular vendor risk reassessment</li>
                  <li>Data processing agreements for all vendors handling personal data</li>
                </ul>
              </section>

              <section id="physical-security" className="policy-section">
                <h2>10. Physical Security</h2>
                <p>
                  While JuryView operates primarily on cloud infrastructure, physical security measures
                  are maintained for our corporate offices and any physical access points:
                </p>
                <ul>
                  <li>Badge-controlled access to office facilities</li>
                  <li>Visitor management and escort requirements</li>
                  <li>Clean desk policy for sensitive information</li>
                  <li>Secure disposal of physical media and documents</li>
                </ul>
                <p>
                  Our cloud providers maintain comprehensive physical security programs including 24/7
                  security personnel, biometric access controls, and environmental controls at all
                  data center facilities.
                </p>
              </section>

              <section id="business-continuity" className="policy-section">
                <h2>11. Business Continuity & Disaster Recovery</h2>

                <h3>11.1 Availability</h3>
                <ul>
                  <li>99.9% uptime SLA for production services</li>
                  <li>Multi-region deployment with automatic failover</li>
                  <li>Load balancing and auto-scaling for traffic spikes</li>
                  <li>Real-time health monitoring and alerting</li>
                </ul>

                <h3>11.2 Backup & Recovery</h3>
                <ul>
                  <li>Automated daily backups with geographic redundancy</li>
                  <li>Point-in-time recovery capability</li>
                  <li>Regular backup restoration testing</li>
                  <li>Recovery time objective (RTO): 4 hours</li>
                  <li>Recovery point objective (RPO): 1 hour</li>
                </ul>

                <h3>11.3 Disaster Recovery</h3>
                <p>
                  Our disaster recovery plan is tested annually through tabletop exercises and
                  live failover drills. Documentation is maintained and updated following each test.
                </p>
              </section>

              <section id="responsible-disclosure" className="policy-section">
                <h2>12. Responsible Disclosure</h2>
                <p>
                  We appreciate the security research community and welcome responsible disclosure
                  of potential vulnerabilities. If you discover a security issue, please report it to:
                </p>
                <div className="contact-info">
                  <p><strong>Security Team</strong></p>
                  <p>Email: <a href="mailto:security@juryview.com">security@juryview.com</a></p>
                </div>
                <p>When reporting, please include:</p>
                <ul>
                  <li>Description of the vulnerability</li>
                  <li>Steps to reproduce the issue</li>
                  <li>Potential impact assessment</li>
                  <li>Any supporting evidence (screenshots, logs, etc.)</li>
                </ul>
                <p>
                  We commit to acknowledging reports within 48 hours and providing updates on
                  remediation progress. We will not pursue legal action against researchers who
                  follow responsible disclosure practices.
                </p>
              </section>

              <div className="policy-footer">
                <p>
                  Questions about our security practices? Contact our security team at{' '}
                  <a href="mailto:security@juryview.com">security@juryview.com</a> or review our{' '}
                  <Link to="/privacy">Privacy Policy</Link> for information about data handling.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SecurityPage;
